import type { Locale } from "@/lib/i18n";
import { quoteSearch } from "@/lib/quote-links";
import samuelPhoto80 from "@/assets/samuel-noriega-80.webp";
import samuelPhoto144 from "@/assets/samuel-noriega-144.webp";

const LINKEDIN_URL = "https://www.linkedin.com/in/samuelnoriega/";

const AUTHOR_PATH = {
  en: "/about/samuel-noriega",
  es: "/es/nosotros/samuel-noriega",
} as const;

const COPY = {
  en: {
    by: "By",
    name: "Samuel Noriega",
    title: "Founder, Shugert Marketing",
    bio: "Samuel leads Shopify performance, CRO and technical optimization projects for DTC brands across the U.S. and LATAM. His team focuses on fixing what directly impacts revenue, not just traffic or design.",
    aboutHeading: "About the author",
    credentialsHeading: "Credentials",
    credentials: [
      "Shopify Partner since 2015",
      "750+ Shopify engagements completed",
      "10+ years building DTC commerce in US, Mexico & LATAM",
      "Specialist in Liquid, Hydrogen, Headless and CRO",
    ],
    published: "Published",
    context: "This article is based on real implementation work with Shopify stores.",
    ctaBody: "If your store is facing similar issues, we can review it and send a clear plan.",
    ctaLabel: "Request a scope",
    ctaHref: "/get-a-quote",
    linkedinAria: "Samuel Noriega on LinkedIn",
    linkedinLabel: "LinkedIn",
  },
  es: {
    by: "Por",
    name: "Samuel Noriega",
    title: "Fundador, Shugert Marketing",
    bio: "Samuel lidera proyectos de performance, CRO y optimización técnica en Shopify para marcas DTC en EE.UU. y LATAM. Su equipo se enfoca en arreglar lo que afecta directamente al revenue, no solo tráfico o diseño.",
    aboutHeading: "Sobre el autor",
    credentialsHeading: "Credenciales",
    credentials: [
      "Shopify Partner desde 2015",
      "750+ proyectos Shopify completados",
      "10+ años construyendo ecommerce DTC en US, México y LATAM",
      "Especialista en Liquid, Hydrogen, Headless y CRO",
    ],
    published: "Publicado",
    context: "Este artículo está basado en trabajo real de implementación con tiendas Shopify.",
    ctaBody:
      "Si tu tienda enfrenta problemas similares, podemos revisarla y enviarte un plan claro.",
    ctaLabel: "Solicitar scope",
    ctaHref: "/es/cotizacion",
    linkedinAria: "Samuel Noriega en LinkedIn",
    linkedinLabel: "LinkedIn",
  },
} as const;

function Avatar({ size }: { size: number }) {
  const small = size <= 40;
  const src = small ? samuelPhoto80 : samuelPhoto144;
  return (
    <img
      src={src}
      srcSet={small ? `${samuelPhoto80} 1x, ${samuelPhoto144} 2x` : `${samuelPhoto144} 1x, ${samuelPhoto144} 2x`}
      alt="Samuel Noriega"
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      className="shrink-0 rounded-full object-cover ring-1 ring-border"
      style={{ width: size, height: size }}
    />
  );
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.37V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 110-4.12 2.06 2.06 0 010 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
    </svg>
  );
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(date: Date, locale: Locale): string {
  // Deterministic SSR-safe formatter (avoids React #418 hydration mismatch
  // caused by Intl.DateTimeFormat differing between workerd and the browser).
  const MONTHS_EN_SHORT = [
    "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec",
  ];
  const MONTHS_ES_SHORT = [
    "ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic",
  ];
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const day = date.getUTCDate();
  return locale === "es"
    ? `${day} ${MONTHS_ES_SHORT[m]} ${y}`
    : `${MONTHS_EN_SHORT[m]} ${day}, ${y}`;
}

function DateLine({
  publishedAt,
  locale,
  className,
}: {
  publishedAt: Date | null;
  locale: Locale;
  className?: string;
}) {
  const t = COPY[locale];
  if (!publishedAt) return null;
  return (
    <p className={className}>
      <span className="text-muted-foreground">{t.published} </span>
      <time dateTime={publishedAt.toISOString()} className="text-foreground/80">
        {formatDate(publishedAt, locale)}
      </time>
    </p>
  );
}

export function AuthorCard({
  variant,
  locale,
  publishedAt,
  reviewedAt,
}: {
  variant: "byline" | "full";
  locale: Locale;
  publishedAt?: string | Date | null;
  reviewedAt?: string | Date | null;
}) {
  const t = COPY[locale];
  const published = toDate(publishedAt);
  void reviewedAt;

  if (variant === "byline") {
    return (
      <div className="mt-8 flex items-start gap-3">
        <Avatar size={40} />
        <div className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-muted-foreground">{t.by}</span>
            <a
              href={AUTHOR_PATH[locale]}
              className="font-medium text-foreground underline-offset-4 hover:underline"
              rel="author"
            >
              {t.name}
            </a>
            <span className="hidden text-muted-foreground sm:inline">· {t.title}</span>
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="nofollow noopener noreferrer me author"
              aria-label={t.linkedinAria}
              className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <LinkedInIcon className="h-3.5 w-3.5" />
            </a>
          </div>
          <DateLine
            publishedAt={published}
            locale={locale}
            className="text-xs text-muted-foreground"
          />
        </div>
      </div>
    );
  }

  return (
    <aside className="mt-16 border-t border-border pt-8">
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {t.aboutHeading}
      </p>
      <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:gap-6">
        <Avatar size={72} />
        <div className="flex-1">
          <p className="font-display text-xl font-semibold text-foreground">
            <a
              href={AUTHOR_PATH[locale]}
              className="underline-offset-4 hover:underline"
              rel="author"
            >
              {t.name}
            </a>
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t.title}
            <span className="mx-2 text-muted-foreground/60">·</span>
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="nofollow noopener noreferrer me author"
              aria-label={t.linkedinAria}
              className="inline-flex items-center gap-1 text-foreground/80 underline-offset-4 hover:underline"
            >
              <LinkedInIcon className="h-3.5 w-3.5" />
              <span>{t.linkedinLabel}</span>
            </a>
          </p>

          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{t.bio}</p>

          <div className="mt-5">
            <p className="text-xs font-medium uppercase tracking-[0.14em] text-foreground/70">
              {t.credentialsHeading}
            </p>
            <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
              {t.credentials.map((item) => (
                <li key={item} className="flex gap-2">
                  <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/40" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <DateLine
            publishedAt={published}
            locale={locale}
            className="mt-5 text-xs"
          />

          <div className="mt-6 flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground">{t.ctaBody}</p>
            <a
              href={`${t.ctaHref}?${new URLSearchParams(quoteSearch({ source: "article" }) as Record<string, string>).toString()}`}
              className="inline-flex shrink-0 items-center justify-center rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background transition-opacity hover:opacity-90"
            >
              {t.ctaLabel} →
            </a>
          </div>
        </div>
      </div>
    </aside>
  );
}
